"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FINAL_TEXT = "Notzz";
const SCRAMBLE_CHARS = "!<>-_\\/[]{}—=+*^?#$%&";
const SCRAMBLE_DURATION = 400;
const HOLD_DURATION = 250;
const SAFETY_TIMEOUT = 3000;

function useScrambleText(finalText: string, duration: number) {
  const [text, setText] = useState(finalText);
  const [resolved, setResolved] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    setText(
      finalText
        .split("")
        .map((c) =>
          c === " " ? " " : SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]
        )
        .join("")
    );
  }, [finalText]);

  useEffect(() => {
    if (!isClient) return;

    let frame = 0;
    const frameTime = 25;
    const totalFrames = Math.round(duration / frameTime);

    const interval = setInterval(() => {
      frame++;
      const revealCount = Math.floor((frame / totalFrames) * finalText.length);

      const newText = finalText
        .split("")
        .map((char, i) => {
          if (char === " ") return " ";
          if (i < revealCount) return char;
          return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        })
        .join("");

      setText(newText);

      if (frame >= totalFrames) {
        clearInterval(interval);
        setText(finalText);
        setResolved(true);
      }
    }, frameTime);

    return () => clearInterval(interval);
  }, [finalText, duration, isClient]);

  return { text, resolved };
}

export function LoadingOverlay({ visible = true, onFinish }: { visible?: boolean; onFinish?: () => void }) {
  const { text, resolved } = useScrambleText(FINAL_TEXT, SCRAMBLE_DURATION);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!resolved || !visible) return;
    const holdTimer = setTimeout(() => setExiting(true), HOLD_DURATION);
    return () => clearTimeout(holdTimer);
  }, [resolved, visible]);

  useEffect(() => {
    if (!visible) setExiting(true);
  }, [visible]);

  useEffect(() => {
    if (!exiting) return;
    const safetyTimer = setTimeout(() => {
      onFinish?.();
    }, SAFETY_TIMEOUT);
    return () => clearTimeout(safetyTimer);
  }, [exiting, onFinish]);

  return (
    <AnimatePresence onExitComplete={onFinish}>
      {visible && !exiting ? (
        <motion.div
          key="loading-overlay"
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            filter: "blur(12px)",
          }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-background text-foreground"
        >
          <motion.span
            animate={{ letterSpacing: resolved ? "0.04em" : "0.15em" }}
            exit={{
              opacity: 0,
              y: -20,
              filter: "blur(8px)",
            }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{ fontFamily: "var(--font-display)" }}
            className="select-none text-5xl font-medium text-foreground"
          >
            {text}
          </motion.span>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}